import { useState, type JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import { fmtDuration } from '../shell/format';

/** Scenario brief, objectives and countdown. Hidden in free-run sandbox sessions. */
export function ScenarioBar(): JSX.Element | null {
  const scenario = useEngine((s) => s.snap?.scenario);
  const [open, setOpen] = useState(true);
  if (!scenario) return null;

  const remaining = Math.max(0, scenario.durationS - scenario.elapsedS);
  const met = scenario.objectives.filter((o) => o.met).length;

  return (
    <div style={{ background: 'var(--panel)', borderBottom: '1px solid var(--line-dim)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 10px' }}>
        <button onClick={() => setOpen(!open)} style={{ padding: '1px 7px' }}>
          {open ? '▾' : '▸'}
        </button>
        <strong style={{ fontSize: 12.5 }}>{scenario.name}</strong>
        <span className="muted num" style={{ fontSize: 11.5 }}>
          {met}/{scenario.objectives.length} objectives · {fmtDuration(remaining)} left
        </span>
        {scenario.announcements.slice(-1).map((a, i) => (
          <span key={i} style={{ color: 'var(--info)', fontSize: 12 }}>
            ⓘ {a.text}
          </span>
        ))}
        <span style={{ flex: 1 }} />
        {scenario.complete && <strong style={{ color: 'var(--ok)' }}>Shift complete</strong>}
      </div>
      {open && (
        <div style={{ padding: '0 10px 8px 40px', display: 'flex', gap: 22, flexWrap: 'wrap' }}>
          <p className="muted" style={{ margin: 0, maxWidth: 620, fontSize: 12, lineHeight: 1.5 }}>
            {scenario.brief}
          </p>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
            {scenario.objectives.map((o) => (
              <li
                key={o.id}
                style={{
                  color: o.failed ? 'var(--pri-high)' : o.met ? 'var(--ok)' : 'var(--text-dim)',
                  textDecoration: o.failed ? 'line-through' : undefined,
                  marginBottom: 2,
                }}
              >
                {o.text}
                {o.kind === 'maintain' && o.toleranceS > 0 && o.violationS > 0 && !o.failed && (
                  <span className="muted num" style={{ fontSize: 10.5 }}>
                    {' '}
                    ({o.violationS.toFixed(0)} of {o.toleranceS} s used)
                  </span>
                )}
              </li>
            ))}
          </ul>
          {scenario.hint && (
            <div style={{ color: 'var(--info)', fontSize: 12, maxWidth: 360 }}>Hint: {scenario.hint}</div>
          )}
        </div>
      )}
    </div>
  );
}
